// 🎯 API Service para Migración del Sistema
// Maneja todas las llamadas a los endpoints de migración del backend

import axios from 'axios';
import { getJwtToken } from '../utils/auth';
import { getBaseUrl } from '../utils/apiClient';

const migrationApiClient = axios.create({
    baseURL: `${getBaseUrl()}/admin/migration`,
    timeout: 30000, // 30 segundos timeout para operaciones de migración
    withCredentials: true, // Incluir cookies y credenciales
});

// Interceptor para agregar token JWT y headers CORS
migrationApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar headers para manejar CORS
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    return config;
}, (error) => {
    return Promise.reject(error);
});

// Interceptor para manejar errores de CORS y otros errores de red
migrationApiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.code === 'ERR_NETWORK' || error.message.includes('CORS')) {
            console.error('Error de CORS o red:', error);
            throw new Error('Error de conexión con el servidor. Verifica que el backend esté corriendo y configurado correctamente.');
        }
        return Promise.reject(error);
    }
);

// 🔧 ENDPOINTS GENERALES
export const getMigrationStatus = async () => {
    try {
        const response = await migrationApiClient.get('/status');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getMigrationSummary = async () => {
    try {
        const response = await migrationApiClient.get('/summary');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const migrateAll = async () => {
    try {
        const response = await migrationApiClient.post('/migrate-all');
        return response.data;
    } catch (error) {
        throw error;
    }
};

// 📂 ENDPOINTS DE CATEGORÍAS
export const migrateAllCategories = async () => {
    try {
        const response = await migrationApiClient.post('/categories');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const checkCategoriesStatus = async () => {
    try {
        const response = await migrationApiClient.get('/categories/check');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const migrateCategoryById = async (categoryId) => {
    try {
        const response = await migrationApiClient.post(`/category/${categoryId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const migrateCategoryByNameAndType = async (name, type) => {
    try {
        const response = await migrationApiClient.post(`/category/${name}/type/${type}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const checkCategoryByNameAndType = async (name, type) => {
    try {
        const response = await migrationApiClient.get(`/category/${name}/type/${type}/check`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// 👥 ENDPOINTS DE PROVEEDORES
export const migrateAllProviders = async () => {
    try {
        const response = await migrationApiClient.post('/providers');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProvidersStats = async () => {
    try {
        const response = await migrationApiClient.get('/providers/stats');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const migrateProviderByUsername = async (username, type) => {
    try {
        const response = await migrationApiClient.post(`/provider/${username}?type=${type}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const checkProviderByUsername = async (username, type) => {
    try {
        const response = await migrationApiClient.get(`/provider/${username}/check?type=${type}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// 📍 ENDPOINTS DE LOCATIONS
export const migrateAllLocations = async () => {
    try {
        const response = await migrationApiClient.post('/locations');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const checkLocationsStatus = async () => {
    try {
        const response = await migrationApiClient.get('/locations/check');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const migrateLocationByName = async (locationName) => {
    try {
        const response = await migrationApiClient.post(`/location/${locationName}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export default migrationApiClient;
